import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateUUID } from '@payer-admin-ui/components-v2/src/utils/strings';
import type { MerchantAdminState } from './store';

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

export type SnackbarItem = {
  id: string;
  message: string;
  severity: SnackbarSeverity;
  duration?: number | null;
};

type SnackbarState = {
  open: boolean;
  current: SnackbarItem | null;
  queue: SnackbarItem[];
};

const initialState: SnackbarState = {
  open: false,
  current: null,
  queue: [],
};

const snackbarSlice = createSlice({
  name: 'snackbar',
  initialState,
  reducers: {
    showSnackbar: {
      reducer: (state, action: PayloadAction<SnackbarItem>) => {
        state.queue.push(action.payload);
        if (!state.open && !state.current) {
          state.current = state.queue.shift() ?? null;
          state.open = !!state.current;
        }
      },
      prepare: (message: string, options?: { severity?: SnackbarSeverity; duration?: number | null }) => ({
        payload: {
          id: generateUUID(),
          message,
          severity: options?.severity ?? 'info',
          duration: options?.duration ?? 6000,
        } as SnackbarItem,
      }),
    },
    closeSnackbar: state => {
      state.open = false;
    },
    onSnackbarExited: state => {
      state.current = state.queue.shift() ?? null;
      state.open = !!state.current;
    },
    clearQueue: state => {
      state.queue = [];
    },
  },
});

export const { showSnackbar, closeSnackbar, onSnackbarExited, clearQueue } = snackbarSlice.actions;

export default snackbarSlice.reducer;

export const selectSnackbarOpen = (state: MerchantAdminState) => state.snackbar.open;
export const selectSnackbarCurrent = (state: MerchantAdminState) => state.snackbar.current;
